export const SPREAD_MODES = ['solo', 'group', 'both'] as const;
export type SpreadMode = (typeof SPREAD_MODES)[number];

export const SPREAD_MODE_LABELS: Record<SpreadMode, string> = {
  solo: 'Индивидуально',
  group: 'В группе',
  both: 'Индивидуально или в группе',
};

/**
 * `preset` spreads have fixed positions (§21, §22).
 * `freeform` is the open working table — «Авторская сессия» (§23).
 */
export const SPREAD_LAYOUTS = ['preset', 'freeform'] as const;
export type SpreadLayout = (typeof SPREAD_LAYOUTS)[number];

/**
 * The instruction that comes with a printed spread.
 *
 * A preset spread is not a list of four slots: the booklet tells the person how
 * to draw, what to ask at each card, and — for «Моё место под солнцем» — what
 * formula the four cards add up to. Without it the page reads as a quiz with
 * four answer options.
 *
 * Kept structured rather than as one blob of prose so each named block keeps
 * its heading, and so an admin can edit one part of it.
 */
export type SpreadGuidanceNote = {
  /** Heading of a named block, e.g. «Формула», «Работа с ролями». */
  title?: string;
  body: string;
};

export type SpreadGuidance = {
  /** What the spread is for, in the booklet's words. */
  intent?: string;
  /** How the cards are drawn. */
  draw?: string;
  /** Questions for the spread as a whole, when they are not per position. */
  questions?: string[];
  notes?: SpreadGuidanceNote[];
};

/**
 * Where a position sits on the spread's own board, counted in cells from the
 * top-left one (§20 — «x/y or layout metadata if needed»).
 *
 * Percentages alone could not say it. A cell centre on an eight-by-eight board
 * falls on 6.25 %, 18.75 % and so on, and `x`/`y` are whole percent — rounding
 * left every card a few pixels off its square, which on a drawn grid reads as a
 * misprint. The cell is the truth; `x`/`y` stay as the rounded position for
 * everything that only needs a point on the canvas.
 */
export type SpreadPositionCell = { col: number; row: number };

/**
 * A small piece of product art standing for the position — a chess figure, say.
 *
 * The platform does not know what a bishop is: it is handed a picture and the
 * word that names it, and shows them beside the position. Whatever the next
 * product brings — a rune, a suit, a planet — arrives the same way, without a
 * component learning its vocabulary.
 */
export type SpreadPositionMarker = { image: string; label: string };
